import { Button, ButtonMode, ButtonSize, Checkbox, Icon, ICONS, Popup, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { difference, sortBy, uniq } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useGlobalModalContext, MODAL_TYPES } from '../../../components/GlobalModal';
import WatchlistPopupFooter from '../../../components/AddToWatchlist/WatchlistPopupFooter';
import { useToast } from '../../../hooks/toast/useToast';
import { useFetchWatchLists, useUpdateWatchList } from '../../../hooks/watchLists';
import { WatchList } from '../../../models/WatchList';
import {
  getIsUserLimitExceeded,
  getIsWatchlistLimitExceeded,
  ISSUERS_LIMIT_PER_WATCHLIST,
  USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE,
  WATCHLIST_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE,
  WATCHLIST_LIMIT_WOULD_EXCEED_MESSAGE_COMPANY_PAGE,
} from '../../MyContent/WatchlistsTab/Watchlist/Watchlist.logic';
import styles from '../../WachlistPopup.module.scss';

const cx = classNames.bind(styles);

interface CompanyAddToWatchlistActionProps {
  companies: string[];
  isLoading: boolean;
  hidePopupOnScroll?: boolean;
  popupClassName?: string;
}

const AddToWatchlistMenu = ({
  companies,
  popupClassName,
  hidePopupOnScroll,
  isLoading,
}: CompanyAddToWatchlistActionProps): React.ReactElement => {
  const { data: watchLists, isLoading: isLoadingWatchlists } = useFetchWatchLists();
  const { mutate: updateWatchlist, isLoading: isUpdatingWatchlist } = useUpdateWatchList();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { showToast } = useToast();
  const { showModal } = useGlobalModalContext();
  const isUserLimitExceeded = getIsUserLimitExceeded(watchLists);
  const sortedWatchlists = useMemo(() => sortBy(watchLists, (list) => list.name.toLowerCase()), [watchLists]);
  const hasWatchlists = watchLists && watchLists.length > 0;

  const handleCheckboxToggle = (watchList: WatchList, isChecked: boolean): void => {
    let watchListTags = watchList.tags;
    let companiesAdded = true;
    if (isChecked) {
      watchListTags = uniq(watchListTags.concat(companies));
    } else {
      companiesAdded = false;
      watchListTags = difference(watchList.tags, companies);
    }

    updateWatchlist(
      {
        id: watchList.id,
        watchList: {
          ...watchList,
          tags: watchListTags,
        },
      },
      {
        onSuccess: () => {
          showToast(
            <span>
              {companiesAdded
                ? 'Companies were successfully added to your watchlist'
                : 'Companies were successfully removed from your watchlist'}
            </span>
          );
        },
      }
    );
  };

  const onCreateNewWatchlistClick = useCallback(() => {
    showModal(MODAL_TYPES.CREATE_WATCHLIST, {
      watchlists: watchLists,
      issuerTags: companies,
    });
    setPopupOpen(false);
  }, [showModal, watchLists, companies]);
  const popupTriggerLabel = `Add to Watchlist${companies.length > 0 ? ` (${companies.length})` : ''}`;

  const popupTrigger = (
    <Button
      loading={isLoadingWatchlists || isUpdatingWatchlist || isLoading}
      disabled={companies.length === 0 || companies.length > ISSUERS_LIMIT_PER_WATCHLIST || isLoading}
      mode={ButtonMode.PRIMARY}
      size={ButtonSize.SMALL}
      ariaLabel={popupTriggerLabel}
      className="no-print"
      data-testid="add-to-watchlist"
    >
      <Icon width={12} height={12} name={ICONS.Plus} />
      {popupTriggerLabel}
    </Button>
  );
  const renderCheckbox = (watchList: WatchList) => {
    const issuerDifference = difference(companies, watchList.tags);
    const isIndeterminate = issuerDifference.length !== 0 && issuerDifference.length < companies.length;
    const isChecked = issuerDifference.length === 0;
    const isWatchlistLimitExceeded = getIsWatchlistLimitExceeded(watchList);
    const wouldWatchlistLimitExceed = getIsWatchlistLimitExceeded(watchList, companies.length);
    const isCheckboxDisabled =
      !isChecked && (isUserLimitExceeded || isWatchlistLimitExceeded || wouldWatchlistLimitExceed);

    const getTooltipMessage = () => {
      if (isUserLimitExceeded) {
        return USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE;
      }
      if (isWatchlistLimitExceeded) {
        return WATCHLIST_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE;
      }
      if (wouldWatchlistLimitExceed) {
        return WATCHLIST_LIMIT_WOULD_EXCEED_MESSAGE_COMPANY_PAGE;
      }
      return null;
    };

    return (
      <Tooltip key={watchList.id} content={getTooltipMessage()} disabled={!isCheckboxDisabled}>
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          className={cx('checkbox', { 'active-tooltip': isCheckboxDisabled })}
          dataTestId="add-remove-issuer-checkbox"
          disabled={isCheckboxDisabled}
          label={`${watchList.name}${watchList.tags.length > 0 ? ` (${watchList.tags.length})` : ''}`}
          onChange={() => handleCheckboxToggle(watchList, !isChecked)}
        />
      </Tooltip>
    );
  };
  return (
    <Tooltip
      trigger={
        <span>
          <Popup
            className={cx('dropdown', popupClassName)}
            trigger={popupTrigger}
            on="click"
            open={isPopupOpen}
            onOpen={() => setPopupOpen(true)}
            onClose={() => setPopupOpen(false)}
            data-testid="companies-add-to-watchlist-popup"
            hideOnScroll={hidePopupOnScroll}
          >
            {hasWatchlists && <div className={cx('popup-content')}>{sortedWatchlists?.map(renderCheckbox)}</div>}
            <WatchlistPopupFooter
              hasWatchlists={hasWatchlists}
              isUserLimitExceeded={isUserLimitExceeded}
              onCreateNewWatchlistClick={onCreateNewWatchlistClick}
            />
          </Popup>
        </span>
      }
      disabled={companies.length <= ISSUERS_LIMIT_PER_WATCHLIST}
      content={`Add up to ${ISSUERS_LIMIT_PER_WATCHLIST} companies to a watchlist`}
    />
  );
};

export default AddToWatchlistMenu;
